import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faXmark, faClockRotateLeft, faCalendarDay, faEraser, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { AuditService } from '../../core/services/audit/audit.service';
import { GroupedAuditRecord } from '../../core/models/audit.model';
import { formatAuditDateValue } from '../../core/utils/date-only.util';

@Component({
  selector: 'app-historico-drawer',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, CalendarModule, FormsModule],
  templateUrl: './historico-drawer.component.html',
  styleUrl: './historico-drawer.component.scss',
})
export class HistoricoDrawerComponent implements OnChanges {
  @Input() open = false;
  @Input() clientId: any;
  @Input() snapshotDate: Date | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() dateSelected = new EventEmitter<Date | null>();

  faXmark = faXmark;
  faClockRotateLeft = faClockRotateLeft;
  faCalendarDay = faCalendarDay;
  faEraser = faEraser;
  faChevronRight = faChevronRight;

  selectedDate: Date | null = null;
  allRecords: GroupedAuditRecord[] = [];
  loading = false;
  expandedIds = new Set<number>();

  constructor(private auditService: AuditService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['snapshotDate'] && this.snapshotDate === null && this.selectedDate !== null) {
      this.selectedDate = null;
    }
    if ((changes['clientId'] || changes['open']) && this.open && this.clientId) {
      this.loadHistory();
    }
  }

  get filteredRecords(): GroupedAuditRecord[] {
    let records = this.allRecords;

    // Hide side-effect entries that were created within a few seconds of a
    // corporate operation (they belong to the operation and should not appear
    // as separate lines). This is the frontend safety net for the backend
    // source_operation_id filter.
    const opRecords = records.filter(r => r.entityType === 'client_corporate_operation');
    if (opRecords.length > 0) {
      const opTimestamps = opRecords.map(r => new Date(r.date).getTime());
      records = records.filter(r => {
        if (r.entityType === 'client_corporate_operation') return true;
        const t = new Date(r.date).getTime();
        return !opTimestamps.some(opT => Math.abs(t - opT) < 10_000);
      });
    }

    if (!this.selectedDate) return records;
    const cutoff = this.endOfSelectedDay()!;
    return records.filter(r => new Date(r.date) <= cutoff);
  }

  /** El calendario da las 00:00; "hasta el día D" incluye todo el día D. */
  private endOfSelectedDay(): Date | null {
    const d = this.selectedDate;
    return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) : null;
  }

  formatAuditValue(val: string | null | undefined): string {
    if (val == null || val === '') return '—';
    // Normalize boolean-like strings to consistent display
    const lower = val.toLowerCase();
    if (lower === 'true' || lower === 'sí' || lower === 'si') return 'Sí';
    if (lower === 'false' || lower === 'no') return 'No';
    const date = formatAuditDateValue(val);
    if (date) return date;
    // JSON object/array — try to extract a meaningful label or show as formatted text
    if ((val.startsWith('{') || val.startsWith('[')) && val.length > 1) {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) {
          if (parsed.length === 0) return '—';
          // Array of primitives
          if (parsed.every(v => typeof v !== 'object' || v === null)) {
            return parsed.join(', ');
          }
          return `${parsed.length} elemento${parsed.length !== 1 ? 's' : ''}`;
        }
        if (parsed && typeof parsed === 'object') {
          // Entity reference with name
          if (parsed.name) return String(parsed.name);
          if (parsed.label) return String(parsed.label);
          return JSON.stringify(parsed, null, 2);
        }
      } catch {
        // not valid JSON, fall through
      }
    }
    return val;
  }

  toggleExpand(id: number): void {
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedIds.has(id);
  }

  isExpandable(record: GroupedAuditRecord): boolean {
    if (record.entityType === 'client_corporate_operation') return false;
    return record.changes.length > 0;
  }

  getDetailHeaderLabel(action: string): { before: string; after: string } | null {
    if (action === 'UPDATE') return { before: 'Antes', after: 'Después' };
    if (action === 'CREATE') return { before: '', after: 'Valor' };
    if (action === 'DELETE') return { before: 'Valor', after: '' };
    return null;
  }

  async onDateChange(date: Date | null) {
    this.selectedDate = date;
    this.dateSelected.emit(date);
    if (this.clientId) {
      await this.loadHistory();
    }
  }

  clearDate() {
    this.selectedDate = null;
    this.dateSelected.emit(null);
    if (this.clientId) {
      this.loadHistory();
    }
  }

  onOverlayClick(event: MouseEvent) {
    if ((event.target as HTMLElement).classList.contains('hd-overlay')) {
      this.close.emit();
    }
  }

  private async loadHistory() {
    if (!this.clientId) return;
    this.loading = true;
    this.expandedIds.clear();
    try {
      const result = await this.auditService.getClientHistory(+this.clientId, {
        dateTo: this.endOfSelectedDay() ?? undefined,
        limit: 300,
      });
      this.allRecords = result.records;
    } catch (e) {
      this.allRecords = [];
    } finally {
      this.loading = false;
    }
  }
}
